import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Mail, Wrench, RefreshCw, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { companyInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [activeTab, setActiveTab] = useState('enquiries');
  const [enquiries, setEnquiries] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (location.state?.user) return; // user passed from AuthCallback
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(res.data);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        navigate('/');
      }
    };
    checkAuth();
  }, [location.state, navigate]);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const [enq, svc] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/enquiries`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/service-requests`, { withCredentials: true })
      ]);
      setEnquiries(enq.data);
      setServiceRequests(svc.data);
    } catch {
      toast.error('Failed to load submissions.');
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated === true) loadData();
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      toast.success('Signed out.');
      navigate('/');
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-slate-950 pt-20 flex items-center justify-center" data-testid="admin-loading">
        <div className="w-10 h-10 border-2 border-slate-600 border-t-slate-200 rounded-full animate-spin"></div>
      </div>
    );
  }

  const rows = activeTab === 'enquiries' ? enquiries : serviceRequests;

  return (
    <div className="min-h-screen bg-slate-950 pt-20" data-testid="admin-dashboard">
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
            <div>
              <h1 className="text-4xl font-bold text-chrome mb-1">Admin Dashboard</h1>
              <p className="text-slate-400 text-sm" data-testid="admin-user-email">
                Signed in as {user?.name || user?.email}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                data-testid="admin-refresh-button"
                className="px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-700 hover:text-white transition-all duration-200 flex items-center space-x-2"
              >
                <RefreshCw size={16} className={loadingData ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </button>
              <button
                onClick={handleLogout}
                data-testid="admin-signout-button"
                className="px-4 py-2.5 btn-chrome rounded-lg text-sm font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2"
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-3 mb-8">
            <button
              onClick={() => setActiveTab('enquiries')}
              data-testid="admin-tab-enquiries"
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                activeTab === 'enquiries' ? 'btn-chrome shadow-lg' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <Mail size={16} />
              <span>Enquiries</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'enquiries' ? 'bg-slate-900/20' : 'bg-slate-700'}`} data-testid="admin-enquiries-count">
                {enquiries.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('service-requests')}
              data-testid="admin-tab-service-requests"
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                activeTab === 'service-requests' ? 'btn-chrome shadow-lg' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <Wrench size={16} />
              <span>Service Requests</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'service-requests' ? 'bg-slate-900/20' : 'bg-slate-700'}`} data-testid="admin-service-count">
                {serviceRequests.length}
              </span>
            </button>
          </div>

          {/* List */}
          {rows.length === 0 ? (
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-16 text-center" data-testid="admin-empty-state">
              <Inbox className="mx-auto text-slate-600 mb-4" size={48} />
              <p className="text-slate-400">No {activeTab === 'enquiries' ? 'enquiries' : 'service requests'} yet.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-submissions-list">
              {rows.map((item) => (
                <div
                  key={item.id}
                  data-testid={`admin-submission-${item.id}`}
                  className="bg-slate-900 rounded-xl border border-slate-800 hover:border-slate-700 transition-all duration-300 p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-3">
                    <div>
                      <h3 className="text-white font-semibold text-lg">{item.name}</h3>
                      <p className="text-slate-400 text-sm">
                        {item.email}{item.phone ? ` · ${item.phone}` : ''}{item.company ? ` · ${item.company}` : ''}
                      </p>
                    </div>
                    <span className="text-slate-500 text-xs whitespace-nowrap">{formatDate(item.timestamp)}</span>
                  </div>
                  {activeTab === 'enquiries' ? (
                    <>
                      <p className="text-slate-300 text-sm font-medium mb-1">{item.subject}</p>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.message}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-slate-300 text-sm font-medium mb-1 capitalize">Service: {item.service}</p>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.details}</p>
                    </>
                  )}
                  <div className="mt-4 flex justify-end">
                    <a
                      href={`mailto:${item.email}?subject=${encodeURIComponent(
                        'Re: ' + (activeTab === 'enquiries' ? item.subject : `Your ${item.service} service request`)
                      )}&body=${encodeURIComponent(`Hi ${item.name},\n\n`)}`}
                      data-testid={`admin-reply-${item.id}`}
                      className="px-4 py-2 btn-chrome rounded-lg text-xs font-semibold transition-all duration-300 shadow hover:shadow-lg flex items-center space-x-2"
                    >
                      <Mail size={14} />
                      <span>Reply by Email</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Admin;
